<template>
  <div class="filter-price-content">
    <div class="filter-price-content__input-group">
      <UiInput
        :value="formattedPrices[0]"
        :placeholder="fromPricePlaceholder"
        @inputValue="(value: string | number) => updateRawValue(value as string, 0)"
        @focusin="focusInHandler"
        @focusout="() => focusOutHandler(0)"
      />
      <UiInput
        :value="formattedPrices[1]"
        :placeholder="toPricePlaceholder"
        @inputValue="(value: string | number) => updateRawValue(value as string, 1)"
        @focusin="focusInHandler"
        @focusout="() => focusOutHandler(1)"
      />
    </div>
    <RangeInput
      :histogram="categoryStore.histogramPrice"
      :min="categoryStore.minPrice"
      :max="categoryStore.maxPrice"
      :value="rawValue"
      :is-modal="props.isModal"
      @input="updateRangeInput"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { UiInput } from '@experience/core-input-ui';
  import RangeInput from '@/features/filter/price/ui/RangeInput.vue';
  import { tripsterStandard } from '@experience/money-utils';
  import ru from '../i18n/ru.json';
  import { useCategoryStore } from '@/shared/store';
  import { EExperienceFilterType } from '@/widgets/filtersBlock';
  import { useI18n } from '@experience/i18n';

  interface Props {
    isModal?: boolean;
  }

  type TRawPrice = Array<number>;

  const props = withDefaults(defineProps<Props>(), {
    isModal: false,
  });

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const categoryStore = useCategoryStore();

  // Задаем граничные значения фильтра цен
  const priceFilter = categoryStore.filters[EExperienceFilterType.PRICE].price;
  const rawValue = ref<TRawPrice>([
    priceFilter[0] !== null ? priceFilter[0] : categoryStore.minPrice,
    priceFilter[1] !== null ? priceFilter[1] : categoryStore.maxPrice,
  ]);

  const fromPricePlaceholder = computed(() => {
    const priceFmt = categoryStore.minPrice ? tripsterStandard(categoryStore.minPrice) : categoryStore.minPrice;

    return t('price.fromPriceTitle', { price: priceFmt });
  });

  const toPricePlaceholder = computed(() => {
    const priceFmt = categoryStore.maxPrice ? tripsterStandard(categoryStore.maxPrice) : categoryStore.maxPrice;

    return t('price.toPriceTitle', { price: priceFmt });
  });

  // Преобрауем эл-ты rawValue к нужному типографическому виду
  const formattedPrices = computed(() => {
    return rawValue.value.map((price: number) => {
      if (price) {
        return tripsterStandard(price);
      }

      return price;
    });
  });

  // Обновляем store, делаем проверку на конечные значения (чтобы не было срабатываания фильтра сразу)
  const updateStore = () => {
    const firstPrice = rawValue.value[0] === categoryStore.minPrice ? null : rawValue.value[0];
    const secondPrice = rawValue.value[1] === categoryStore.maxPrice ? null : rawValue.value[1];

    categoryStore.setPrice([firstPrice, secondPrice] as [number, number]);
  };

  // Обновляем rawValue после ввода новой цены в input
  const updateRawValue = (value: string, index: number) => {
    const newValueForRaw = [...rawValue.value] as TRawPrice;
    const parseValue = value.replaceAll(/\D/g, '');

    newValueForRaw[index] = parseValue.length ? Number(parseValue) : 0;

    const firstValueInput = newValueForRaw[0];
    const secondValueInput = newValueForRaw[1];
    rawValue.value = [firstValueInput, secondValueInput];
    updateStore();
  };

  const updateRangeInput = (value: Array<number>) => {
    rawValue.value = [...value];
  };

  // логика для очистки инпута при вводе, и валидированном заполнении при расфокусе
  const isFocusInput = ref(false);

  const focusInHandler = () => {
    isFocusInput.value = true;
  };

  const focusOutHandler = (id: number) => {
    isFocusInput.value = false;

    const currentRawValue = rawValue.value[id];

    const isToValue = Boolean(id);

    if (!currentRawValue) {
      rawValue.value[id] = Boolean(id) ? categoryStore.maxPrice : categoryStore.minPrice;

      return;
    }

    if (isToValue) {
      validateToValue();
    } else {
      validateFromValue();
    }
  };

  const saveOrSwapValues = (from: number, to: number) => {
    if (from > to) {
      rawValue.value = [to, from];

      return;
    }

    rawValue.value = [from, to];
  };

  const validateFromValue = () => {
    const [from, to] = rawValue.value;

    saveOrSwapValues(Math.max(from ?? categoryStore.minPrice, categoryStore.minPrice), to);
  };

  const validateToValue = () => {
    const [from, to] = rawValue.value;

    saveOrSwapValues(from, Math.min(to ?? categoryStore.maxPrice, categoryStore.maxPrice));
  };

  watch(
    () => categoryStore.tmpFilters[EExperienceFilterType.PRICE].price,
    ([startPrice, endPrice]: [number, number] | [null, null]) => {
      rawValue.value = [
        startPrice !== null ? startPrice : categoryStore.minPrice,
        endPrice !== null ? endPrice : categoryStore.maxPrice,
      ];
    },
    { immediate: true },
  );
</script>

<style scoped lang="scss">
  .filter-price-content {
    display: flex;
    flex-direction: column;
  }

  .filter-price-content__input-group {
    display: flex;

    :deep(.input__wrap) {
      z-index: 1;

      &:first-child {
        --io-border-radius-top-right: 0;
        --io-border-radius-bottom-right: 0;
        margin-right: -1px;
      }

      &:last-child {
        --io-border-radius-top-left: 0;
        --io-border-radius-bottom-left: 0;
      }

      &:focus-within {
        z-index: 3;
      }
    }
  }
</style>
